// table related components
import { DataTable } from "@/components/ui/table/DataTable";
import { DataTableColumnHeader } from "@/components/ui/table/DataTableColumnHeader";
import { ColumnDef } from "@tanstack/react-table";
import { DataTableRowActions } from "./DataTableRowAction";
import useTableData, { SearchCriteria } from "@/components/ui/table/DataTableLogic";
import TableSearchFilter from "@/components/ui/table/DataTableSearch";
import Pagination from "@/components/ui/table/PaginationTable";
import usePagination from "@/hooks/UsePagination";
import TableFilters from "@/components/ui/table/DataTableFilter";
import { displayDate, displayValue } from "@/utils/Helper";
// ui related components
import { useToast } from "@/components/ui/UseToast";
import { Checkbox } from "@/components/ui/CheckBox";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/MobileToolTip";

// api related components
import { ErrorType } from "@/app/types";
import { useEffect, useMemo, useState } from "react";
import { Status } from "@/app/shared_enums/enums";
import ServiceForm from "../modal/ServiceForm";
import { useGetServiceQuery } from "@/services/ServiceApis";
import { Schedule, Service } from "@/app/types/service";
import { useGetDepartmentListQuery } from "@/services/DepartmentApi";

// initial filter setting
const initialValue = {
  limit: 10,
  offset: 0,
  sort_order: "desc",
  sort_key: "created_at",
};

const ServiceManagementTable = () => {
  //access level
  const service = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string).service ??
        "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  const { toast } = useToast();
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const { data: departmentOptions } = useGetDepartmentListQuery();

  const [openFilter, setOpenFilter] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [filterData, setFilterData] = useState({});
  const [data, setData] = useState<Service | undefined>(undefined);
  const [inputValue, setInputValue] = useState<string>("");
  const { searchCriteria, query, toggleSortOrder, setSearchCriteria } =
    useTableData(initialValue);

  const {
    data: servicesData,
    isLoading: isStaffDataLoading,
    refetch,
    error,
    isError,
  } = useGetServiceQuery(
    { query },
    {
      skip: query == "",
    }
  );

  const tableData = useMemo(() => {
    return Array.isArray(servicesData?.data) ? servicesData?.data : [];
  }, [servicesData?.data]);

  const handleOpenForm = (payload: Service) => {
    setAction("edit");
    setData({ ...payload, id: payload.id });
    setIsDialogOpen(true);
  };

  const actionsColumn: ColumnDef<Service> = {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => (
      <DataTableRowActions
        access={service}
        data={row.original}
        refetch={refetch}
        handleEdit={handleOpenForm}
      />
    ),
  };

  const columns: ColumnDef<Service>[] = [
    {
      id: "select",
      header: ({ table }) => (
        <Checkbox
          checked={table.getIsAllPageRowsSelected()}
          onCheckedChange={(value: boolean) =>
            table.toggleAllPageRowsSelected(!!value)
          }
          aria-label="Select all"
          className="translate-y-[2px]"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value: boolean) => row.toggleSelected(!!value)}
          aria-label="Select row"
          className="translate-y-[2px]"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: "name",
      meta: "name",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Name"
          sortKey="name"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const Value =row?.original.name;
        return (
          <>
            {service === "write" ? (
              <button
                onClick={() => handleOpenForm(row.original)}
                className="underline"
              >
                <Tooltip>
                  <TooltipTrigger>
                    <p className="capitalize underline text-nowrap max-w-[150px] truncate overflow-hidden">
                      {displayValue(row.original.name)}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>{row.original.name}</TooltipContent>
                </Tooltip>
              </button>
            ) : (
              <>
                <Tooltip>
                  <TooltipTrigger>
                    <p className="capitalize text-nowrap max-w-[150px] truncate overflow-hidden">
                      {displayValue(row.original.name)}
                    </p>
                  </TooltipTrigger>
                  <TooltipContent>{row.original.name}</TooltipContent>
                </Tooltip>
              </>
            )}
          </>
        );
      },
    },
    {
      accessorKey: "service_code",
      meta: "service_code",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Service Code"
          sortKey="id"
          sortable={false}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center gap-4 text-ellipsis capitalize whitespace-nowrap overflow-hidden">
            {displayValue(row?.original.code)}
          </div>
        );
      },
    },
    {
      accessorKey: "schedules",
      meta: "schedules",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Days"
          sortKey="schedules"
          sortable={false}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <Tooltip>
            <TooltipTrigger>
              <p className="capitalize text-nowrap max-w-[150px] truncate overflow-hidden">
                {formatDaysString(row.original.schedules).length > 8
                  ? `${formatDaysString(row.original.schedules).slice(0, 8)}...`
                  : formatDaysString(row.original.schedules)}
              </p>
            </TooltipTrigger>
            <TooltipContent>
              {formatDaysString(row.original.schedules)}
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "department_id",
      meta: "department_id",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Department"
          sortKey="department_id"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        const department = departmentOptions?.find(
          (dept) => dept.value == row.original.department_id
        );
        return (
          <Tooltip>
            <TooltipTrigger>
              <p className="capitalize text-nowrap max-w-[150px] truncate overflow-hidden">
                {displayValue(department?.label)}
              </p>
            </TooltipTrigger>
            <TooltipContent>{department?.label}</TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      accessorKey: "status",
      meta: "status",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Status"
          sortKey="status"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <div className="flex items-center capitalize gap-4 text-ellipsis whitespace-nowrap overflow-hidden">
            <Badge
              className={`
                ${
                  row.original?.status == Status.Active
                    ? "bg-green-600"
                    : "bg-red-500"
                } text-white font-medium`}
            >
              {displayValue(row?.original.status)}
            </Badge>
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      meta: "created_at",
      header: ({ column }) => (
        <DataTableColumnHeader
          column={column}
          title="Created At"
          sortKey="created_at"
          sortable={true}
          searchCriteria={searchCriteria} // Pass current search criteria
          toggleSortOrder={toggleSortOrder}
        />
      ),
      cell: ({ row }) => {
        return (
          <p className="capitalize text-nowrap">
            {new Date(row.original?.created_at + "Z")
              .toLocaleDateString("en-PK", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })
                .replace(/\//g, "-")}
          </p>
        );
      },
    },
    ...(service !== "read" ? [actionsColumn] : []),
  ];
  const totalRecords = servicesData?.filtered_count ?? 0;

  useEffect(() => {
    if (isError) {
      // error state
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error",
        description: `${typedError?.data?.detail ?? (typedError?.data as unknown as { message?: string })?.message}`,
      });
    }
  }, [isError]);

  const {
    handleLimitChange,
    handleNextPage,
    handlePrevPage,
    handleFirstPage,
    handleLastPage,
    isLastPage,
  } = usePagination({
    totalRecords,
    searchCriteria,
    setSearchCriteria,
  });

  const handleOpen = () => {
    setAction("add");
    setIsDialogOpen(true);
  };

  const handleFilterChange = (
    filterName: string,
    value: string,
    operator?: string
  ) => {
    setFilterData((prevFilters: Record<string, string>) => {
      // Create a new filter data object
      const updatedFilters = { ...prevFilters };

      // Construct the new filter key using the operator
      const filterKey = operator ? `${filterName}_${operator}` : filterName;

      // Remove old keys related to the same filter
      Object.keys(updatedFilters).forEach((key) => {
        if (key.startsWith(filterName) && key !== filterKey) {

          setSearchCriteria((prevFilter: any) => {
            const updatedFilters = { ...prevFilter };
            delete updatedFilters[key]
            return updatedFilters
          })
          delete updatedFilters[key]; // Remove old operator keys

        }
      });

      // Add the new key-value pair
      updatedFilters[filterKey] = value;

      return updatedFilters;
    });
  };

  const filterDisplay = [
    {
      type: "select",
      name: "status",
      label: "Status",
      options: [
        { value: "inactive", label: "Inactive" },
        { value: "active", label: "Active" },
      ],
      function: (value: string) => handleFilterChange("status", value),
    },
    {
      type: "select",
      name: "multi_step",
      label: "Multi Step",
      options: [
        { value: "true", label: "Yes" },
        { value: "false", label: "No" },
      ],
      function: (value: string) => handleFilterChange("multi_step", value),
    },
    {
      type: "time_opt",
      name: "ticket_end_time",
      label: "Ticket End Time",
      defaultValue: defaultTimeFilter(filterData, "ticket_end_time"),
      options: [
        { value: "gt", label: "Greater than" },
        { value: "lt", label: "Less than" },
        { value: "eq", label: "Equal to" },
      ],
      function: (value: { operator: string; time: string }) => {
        handleFilterChange("ticket_end_time", value.time, value.operator)
      }
    },
    {
      type: "time_opt",
      name: "closing_time",
      label: "Closing Time",
      defaultValue: defaultTimeFilter(filterData, "closing_time"),
      options: [
        { value: "gt", label: "Greater than" },
        { value: "lt", label: "Less than" },
        { value: "eq", label: "Equal to" },
      ],
      function: (value: { operator: string; time: string }) =>
        handleFilterChange("closing_time", value.time, value.operator),
    },
    {
      type: "time_opt",
      name: "opening_time",
      label: "Opening Time",
      defaultValue: defaultTimeFilter(filterData, "opening_time"),
      options: [
        { value: "gt", label: "Greater than" },
        { value: "lt", label: "Less than" },
        { value: "eq", label: "Equal to" },
      ],
      function: (value: { operator: string; time: string }) =>
        handleFilterChange("opening_time", value.time, value.operator),
    },
  ];
  return (
    <>
      <div className="flex flex-row lg:items-center justify-between gap-4 px-3 ">
        {/* Table Filter component */}
        <TableSearchFilter
          inputValue={inputValue}
          setInputValue={setInputValue}
          setSearchCriteria={setSearchCriteria}
          placeHolder={"Search"}
        />

        {/* Buttons Container */}
        <div className="flex flex-row lg:flex-row lg:justify-center lg:items-center gap-2">
          <Button
            access={service !== "read"}
            className="bg-primary text-sm  text-white flex items-center gap-1  lg:mb-0 h-8 px-2"
            onClick={handleOpen}
          >
            <i className="fa-solid fa-plus" />
            Create New
          </Button>

          <button
            className={`border rounded-full size-3 text-gray-400 p-4 flex items-center justify-center ${Object.keys(filterData).length ? "border-primary" : ""}`}
            onClick={() => setOpenFilter(true)}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <i
                    className={`fa fa-filter ${Object.keys(filterData).length ? "text-primary" : ""}`}
                  ></i>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  <p className="capitalize">click to apply filter</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </button>
        </div>
      </div>
      {/* Data Table */}
      <div className="mt-4 bg-white">
        <DataTable
          columns={columns}
          data={tableData}
          isLoading={isStaffDataLoading}
          searchMessage="No Service Found"
        />
      </div>

      {/* Pagination */}
      {tableData.length > 0 && (
        <Pagination
          limit={searchCriteria.limit}
          offset={searchCriteria.offset}
          totalItems={totalRecords}
          onLimitChange={handleLimitChange}
          onNextPage={handleNextPage}
          onPrevPage={handlePrevPage}
          onFirstPage={handleFirstPage}
          onLastPage={handleLastPage}
          isLastPage={isLastPage}
        />
      )}

      {/* table filters */}
      <TableFilters
        isOpen={openFilter}
        setOpen={setOpenFilter}
        initialValue={initialValue}
        filterData={filterData}
        setFilter={setFilterData}
        setSearchCriteria={setSearchCriteria}
        filterDisplay={filterDisplay}
        setInputValue={setInputValue}
      />

      {/* form */}
      <ServiceForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        action={action}
        setAction={setAction}
        data={data}
        setData={setData}
        refetch={refetch}
      />
    </>
  );
};

export default ServiceManagementTable;


const formatDaysString = (daysArray: Schedule[]) => {
  const days = daysArray.map(({ day }) => day.slice(0, 3));
  return days.join(", ");
};


type InputObject = Record<string, string>;

type TransformedObject = {
  operator: string;
  time: string;
} | null;

function defaultTimeFilter(input: InputObject, name: string): TransformedObject {
  for (const [key, value] of Object.entries(input)) {
    if (key.startsWith(name)) {
      const parts = key.split('_'); // Split the key into parts
      const operator = parts[parts.length - 1]; // Extract the last part as the operator
      return {
        operator,
        time: value,
      };
    }
  }
  return null; // Return null if no matching key is found
}